// @flow strict
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {
  BodyMediumBold,
  BodySmall,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  Card,
  CardFooter,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  Toast,
  TOAST_SEMANTIC,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {useSelector, useDispatch} from 'react-redux';
import {useDrag, useDrop} from 'react-dnd-cjs';
import {selectApplicantInformation} from 'src/selectors/crm';
import css from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-educations/all-educations.css';
import {EducationCard} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-educations/edit-education-card.jsx';
import type {educationType} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-educations/edit-education-card.jsx';
import type {ExperienceType} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-expreriences/edit-experience-card.jsx';

import {ClickableIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';

//$FlowFixMe[nonstrict-import]
import * as reduxApi from 'src/utils/redux-api-v2';


export type FormData = {|
  name: string,
  email: string,
  phone: string,
  yearsExp: string,
  monthsExp: string,
  location: string,
  experiences: Array<ExperienceType>,
  educations: Array<educationType>,
|};
type Props = {|
  setFormData: (
    prevData: FormData | ((prevData: FormData) => FormData),
  ) => void,
|};

const ITEM_TYPE = 'EDUCATION';

export const AllEducations = ({setFormData}: Props): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [addEducationCardOpen, setAddEducationCardOpen] = React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState(-1);
  const applicantDetails = useSelector(selectApplicantInformation);
  const initialEducation = applicantDetails?.resume?.educations ?? [];
  const [educations, setEducations] = React.useState(initialEducation);

  const dispatch = useDispatch();

  const handleEditClick = (index) => {
    setEditingIndex(index);
    setAddEducationCardOpen(true);
  };

  const handleAddEducation = () => {
    setEditingIndex(-1);
    setAddEducationCardOpen(true);
  };

  const moveEducation = (fromIndex, toIndex) => {
    const updatedEducations = [...educations];
    const [movedEducation] = updatedEducations.splice(fromIndex, 1);
    updatedEducations.splice(toIndex, 0, movedEducation);
    setEducations(updatedEducations);
    dispatch(
      reduxApi.post(
        `api/candidates/${applicantDetails.id}`,
        {
          section: 'EDUCATION_LIST',
          editFields: {data: updatedEducations, type: 'EDIT'},
        },
        {},
        {apiPath: '/trmapi/'},
      ),
    )
      .then((res) => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>{`${t(
              'Education_Updated_Successfully',
              'Education Updated Successfully',
            )}!`}</ToastTitle>
          </Toast>,
        );
      })
      .catch((err) => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>{`${t(
              'Error_Updating_Education',
              'Error Updating Education',
            )}!`}</ToastTitle>
          </Toast>,
        );
      });
  };

  React.useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      educations,
    }));
  }, [educations, setFormData]);

  const EducationCardDraggable = ({education, index}) => {
    const ref = React.useRef(null);
    const [{isDragging}, drag, preview] = useDrag({
      type: ITEM_TYPE,
      item: {type: ITEM_TYPE, index},
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [, drop] = useDrop({
      accept: ITEM_TYPE,
      drop: (item) => {
        if (!ref.current) {
          return;
        }
        if (item.index !== index) {
          moveEducation(item.index, index);
          item.index = index;
        }
      },
    });

    preview(drop(ref));

    return (
      <Card
        ref={ref}
        key={index}
        classNames={{wrapper: css.educationCard}}
        style={{opacity: isDragging ? 0.5 : 1}}
      >
        <div>
          <ClickableIcon
            color="primary"
            name="grip-dots-vertical"
            type="regular"
            ref={drag}
          />
        </div>
        <div className={css.educationCardDetails}>
          <div className={css.educationDetails}>
            <BodyMediumBold>
              <TruncatedTextWithTooltip tooltipText={{bodyMaxLines: 4}}>
                {education.educationOrg}
              </TruncatedTextWithTooltip>
            </BodyMediumBold>
            <BodySmall>
              <TruncatedTextWithTooltip tooltipText={{bodyMaxLines: 4}}>
                {education.title}
              </TruncatedTextWithTooltip>
            </BodySmall>
          </div>
          <div>
            <div className={css.educationDetails}>
              <BodyMediumBold>
                <TruncatedTextWithTooltip tooltipText={{bodyMaxLines: 4}}>
                  {formatDate(education.startYear, education.endYear)}
                </TruncatedTextWithTooltip>
              </BodyMediumBold>
              <BodySmall>
                <TruncatedTextWithTooltip tooltipText={{bodyMaxLines: 4}}>
                  {education.location}
                </TruncatedTextWithTooltip>
              </BodySmall>
            </div>
          </div>
        </div>
        <div>
          <Button
            iconRightName="pen"
            type="ghost"
            onClick={() => handleEditClick(index)}
          />
        </div>
      </Card>
    );
  };

  const formatDate = (startYear, endYear) => {
    if (startYear === -1 && endYear === -1) {
      return '';
    }
    return `${startYear || ''} - ${endYear === -1 ? 'Present' : endYear || ''}`;
  };

  return (
    <div className={css.resumeFieldsContainer}>
      <div className={css.header}>
        <FormLabelSmall color="secondary">
          {t('Education', 'Education')}
        </FormLabelSmall>
        {educations?.length !== 0 && (
          <Button
            iconLeftName="plus"
            type="secondary"
            onClick={handleAddEducation}
          >
            {t('Add_Education', 'Add Education')}
          </Button>
        )}
      </div>
      <div className={css.emptyStateContainer}>
        {!educations || educations.length === 0 ? (
          addEducationCardOpen ? (
            <div className={css.resumeFieldsContainer}>
              <EducationCard
                setAddEducationCardOpen={setAddEducationCardOpen}
                educations={educations}
                setEducations={setEducations}
              />
            </div>
          ) : (
            <Card>
              <EmptyState
                description={`${t(
                  'Sorry_we_couldn\'t_fetch_this_candidate\'s_education_Please_fill_it_in_manually',
                  'Sorry, we couldn\'t fetch this candidate\'s education. Please fill it in manually',
                )}.`}
                imageVariant="data"
              >
                <Button
                  iconLeftName="plus"
                  type="secondary"
                  onClick={handleAddEducation}
                >
                  {t('Add_Education', 'Add Education')}
                </Button>
              </EmptyState>
            </Card>
          )
        ) : (
          <div className={css.educationWrapper}>
            {addEducationCardOpen && editingIndex === -1 && (
              <div className={css.resumeFieldsContainer}>
                <EducationCard
                  setAddEducationCardOpen={setAddEducationCardOpen}
                  educations={educations}
                  setEducations={setEducations}
                />
              </div>
            )}
            <div className={css.educationCardContainer}>
              {educations.map((education, index) =>
                editingIndex === index && addEducationCardOpen ? (
                  <EducationCard
                    setAddEducationCardOpen={setAddEducationCardOpen}
                    educations={educations}
                    setEducations={setEducations}
                    editIndex={editingIndex}
                  />
                ) : (
                  <EducationCardDraggable
                    key={index}
                    education={education}
                    index={index}
                  />
                ),
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
